import { Icon, IconName, Text } from '@cotera/client/app/components/ui';
import {
  useAction,
  useInScopeModel,
  ModelScope,
} from '@cotera/client/v0/actions/framework';
import { NewColumnAction } from '../dataset/actions/new-column.action';
import { Assert } from '@cotera/utilities';
import { GraphModel } from '../graph/graph.model';
import { DatasetModel } from '../dataset/model/dataset.model';
import { useTabParams } from '../tab-manager/tab-manager';
import { Tab } from '../tab-manager';
import { TabItem } from '../components/topbar/tab-item';

type ColumnType = {
  type: 'llm' | 'expression' | 'taxonomy' | 'tool-call';
  icon: IconName;
  label: string;
  path: string;
  description: string;
};

const COLUMN_TYPES: ColumnType[] = [
  {
    type: 'llm',
    label: 'LLM',
    icon: 'sparkles',
    path: '/llm',
    description: 'Use a LLM to populate a column',
  },
  {
    type: 'expression',
    label: 'Formula',
    icon: 'code-bracket',
    path: '/formula',
    description: 'Use a formula to populate a column',
  },
  {
    type: 'taxonomy',
    label: 'Taxonomy',
    icon: 'list-tree',
    path: '/taxonomy',
    description: 'Use a taxonomy to populate a column',
  },
  {
    type: 'tool-call',
    label: 'Tool Call',
    icon: 'workflow',
    path: '/tool-call',
    description: 'Use a tool call to populate a column',
  },
];

export const NewColumn = () => {
  const { id: dataset, tabId } = useTabParams<{
    id: string;
  }>();

  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = new DatasetModel(
    { name: dataset, graph: graphModel.graph, compiler: graphModel.compiler },
    {
      id: `${dataset}-new-col`, // TODO: warning, this may not be unique
      targeted: true,
    }
  );

  return (
    <>
      <Tab tabId={tabId}>
        <TabItem
          tab={{
            id: tabId,
            title: `${dataset}/New Column`,
            showClose: true,
          }}
        />
      </Tab>
      <ModelScope models={[model]}>
        <NewColumnInner />
      </ModelScope>
    </>
  );
};

const NewColumnInner: React.FC = () => {
  const newColumnAction = useAction(NewColumnAction);

  return (
    <div className="flex flex-col gap-2 h-full w-full justify-center items-center">
      <div className="flex flex-col gap-4">
        <Text.Title type="title" subtitle="Select a column type">
          New Column
        </Text.Title>
        <ul className="flex flex-col gap-2">
          {COLUMN_TYPES.map((columnType) => (
            <li
              key={columnType.path}
              className="hover:bg-accent transition-colors cursor-pointer duration-200 flex flex-row items-center gap-4 rounded-md px-4 py-2 border border-divider"
              onClick={async () => {
                await newColumnAction.execute({
                  type: columnType.type,
                  strategy: 'replace',
                });
              }}
            >
              <div className="rounded-full bg-accent p-2">
                <Icon icon={columnType.icon} />
              </div>
              <div className="flex flex-col gap-1">
                <Text.Title>{columnType.label}</Text.Title>
                <Text.Caption>{columnType.description}</Text.Caption>
              </div>
              <div className="flex flex-row gap-2 ml-auto">
                <Icon icon="plus" />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
